import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import tradingRentalEstimationService, {
  normalizeTradingRentalEstimation,
  TradingRentalEstimation,
  TradingRentalEstimationRequest,
  type TradingRentalLineImportMode,
  type TradingRentalLineType,
} from '@/services/tradingRentalEstimation.service';

export const TR_ESTIMATION_KEYS = {
  all: ['trading-rental-estimations'] as const,
  lists: () => [...TR_ESTIMATION_KEYS.all, 'list'] as const,
  list: (filters: Record<string, unknown> | undefined) => [...TR_ESTIMATION_KEYS.lists(), filters] as const,
  details: () => [...TR_ESTIMATION_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...TR_ESTIMATION_KEYS.details(), id] as const,
  relatedQuotations: (id: string) => [...TR_ESTIMATION_KEYS.all, 'related-quotations', id] as const,
};

export function useTradingRentalEstimations(filters?: Record<string, unknown>) {
  return useQuery({
    queryKey: TR_ESTIMATION_KEYS.list(filters),
    queryFn: () => tradingRentalEstimationService.getAll(filters),
  });
}

export function useTradingRentalEstimation(id: string, options?: { enabled?: boolean }) {
  return useQuery<TradingRentalEstimation>({
    queryKey: TR_ESTIMATION_KEYS.detail(id),
    queryFn: async () => {
      const raw = await tradingRentalEstimationService.getById(id);
      const normalized = normalizeTradingRentalEstimation(raw);
      if (!normalized) {
        throw new Error('Invalid Trading/Rental estimation response');
      }
      return normalized;
    },
    enabled: options?.enabled ?? !!id,
  });
}

export function useTradingRentalRelatedQuotations(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: TR_ESTIMATION_KEYS.relatedQuotations(id),
    queryFn: () => tradingRentalEstimationService.getRelatedQuotations(id),
    enabled: options?.enabled ?? !!id,
  });
}

export function useCreateTradingRentalEstimation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TradingRentalEstimationRequest) => tradingRentalEstimationService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TR_ESTIMATION_KEYS.lists() });
      message.success('Trading/Rental estimation created');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to create estimation');
    },
  });
}

export function useUpdateTradingRentalEstimation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TradingRentalEstimationRequest }) =>
      tradingRentalEstimationService.update(id, data),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: TR_ESTIMATION_KEYS.detail(vars.id) });
      queryClient.invalidateQueries({ queryKey: TR_ESTIMATION_KEYS.lists() });
      message.success('Trading/Rental estimation updated');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to update estimation');
    },
  });
}

export function useDeleteTradingRentalEstimation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tradingRentalEstimationService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TR_ESTIMATION_KEYS.lists() });
      message.success('Trading/Rental estimation deleted');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to delete estimation');
    },
  });
}

export function useCancelTradingRentalEstimation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tradingRentalEstimationService.cancel(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: TR_ESTIMATION_KEYS.detail(id) });
      queryClient.invalidateQueries({ queryKey: TR_ESTIMATION_KEYS.lists() });
      message.success('Trading/Rental estimation cancelled');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to cancel estimation');
    },
  });
}

export function useConvertTradingRentalToQuotation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      selectedLineIds,
      departmentId,
    }: {
      id: string;
      selectedLineIds?: string[];
      departmentId?: string;
    }) => tradingRentalEstimationService.convertToQuotation(id, selectedLineIds, departmentId),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: TR_ESTIMATION_KEYS.detail(vars.id) });
      queryClient.invalidateQueries({ queryKey: TR_ESTIMATION_KEYS.relatedQuotations(vars.id) });
      queryClient.invalidateQueries({ queryKey: ['quotations'] });
      message.success('Quotation created from Trading/Rental estimation');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to convert to quotation');
    },
  });
}

export function useImportTradingRentalLines() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      file,
      lineType,
      mode,
    }: {
      id: string;
      file: File;
      lineType: TradingRentalLineType;
      mode: TradingRentalLineImportMode;
    }) => tradingRentalEstimationService.importLines(id, file, lineType, mode),
    onSuccess: (result, vars) => {
      queryClient.invalidateQueries({ queryKey: TR_ESTIMATION_KEYS.detail(vars.id) });
      queryClient.invalidateQueries({ queryKey: TR_ESTIMATION_KEYS.lists() });
      message.success(`Imported ${result.imported} ${vars.lineType.toLowerCase()} line(s)`);
      if (result.warnings?.length) {
        message.warning(`${result.warnings.length} warning(s): ${result.warnings.slice(0, 2).join('; ')}${result.warnings.length > 2 ? '…' : ''}`);
      }
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to import lines');
    },
  });
}

